import { BaseEntity } from '@/api/EntityApi';
import { DeepKeys } from '@tanstack/react-table';
import { Asset } from '@/api/AssetApi';
import { AttributeValue } from '@/api/AttributeValueApi';
import { Attribute } from '@/api/AttributeApi';
import LANGUAGES from '@/configs/languages';
import APP_CONFIG from '@/configs/appConfig';

export type Language = keyof typeof LANGUAGES;
export type ForeignLanguage = Exclude<Language, 'PL'>;
export type Translations<TData> = Record<ForeignLanguage, TData>;

export type SortOrder = 'ASC' | 'DESC';

export interface Sort<TData extends BaseEntity = any> {
	key: DeepKeys<TData> | null;
	order: SortOrder;
}

export type Iso639Code = (typeof APP_CONFIG)['ISO_639'][number];

export interface ApiQueryOptions<TData extends BaseEntity> {
	page: number;
	limit: number;
	search: string;
	sortBy: Sort<TData>;
}

export type CustomFilters = Record<string, unknown>;

export interface ApiPaginatedResponse<TData extends {} = {}> {
	data: TData[];
	meta: {
		currentPage: number;
		itemsPerPage: number;
		totalItems: number;
		totalPages: number;
	};
}

export interface ApiFormattedResponseData<TData extends {} = {}> {
	data: TData[];
	totalPages: number;
}

export interface CreateManyResponse {
	passed: string[];
	rejected: string[];
}

export interface Attachment {
	id: UUID;
	path: string;
	name: string;
	size: number;
	mimetype: string;
}

export type UUID = ReturnType<typeof crypto.randomUUID>;

export enum CreatorActionType {
	SET_MATERIAL = 'SET_MATERIAL',
	SET_MODEL = 'SET_MODEL',
	CAMERA_FOCUS = 'CAMERA_FOCUS',
	UPDATE_MATERIAL = 'UPDATE_MATERIAL',
}

export type CreatorAction = {
	id: UUID;
	attributeValue: AttributeValue;
	type: CreatorActionType;
	payload: CreatorActionPayload;
};

export type CreatorActionPayload = {
	asset?: Asset;
	target?: string;
};

export type AttributeWithValues = Attribute & {
	attributeValues: AttributeValue[];
};

export type AvailableAttributeValues = {
	attributes: AttributeWithValues[];
	dimensionAttributes: AttributeWithValues[];
};

export interface CreatorParams {
	selectedValues: Record<UUID, UUID | null>;
}
